import {
  EnvironmentOutlined,
  InfoCircleOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { Popover, Spin, Tooltip, Typography } from 'antd';
import { FC, useEffect, useState } from 'react';

import { IRecruiterDashboardSettings } from '../../../hooks/useRecruiterDashboardSettings';
import {
  IRecruiterStatsEntry,
  IUnassignedLocationEntry,
} from '../../../hooks/useRecruiterStats';
import { getInitials, getUserColor } from '../../../utils/avatarUtils';
import { DeltaIndicator } from '../../charts/DeltaIndicator';

const { Text } = Typography;

// ── Colours ──────────────────────────────────────────────
const CARD_BG = '#0e4d8f';
const ROW_BG = '#0b345f';
const ROW_BG_ALT = '#0d3f72';
const LABEL_COLOR = '#a7b4c2';
const VALUE_COLOR = '#fff';
const DIVIDER_COLOR = 'rgba(255,255,255,0.12)';
const MOBILE_BREAKPOINT = 770;

export interface IRegionRecruitStats {
  regionId: string;
  regionName: string;
  recruitedDonorCount: number;
  prevRecruitedDonorCount: number;
  activeLocationCount: number;
  totalLocationCount: number;
}

interface IRecruiterStatsCardProps {
  entries: IRecruiterStatsEntry[];
  loading: boolean;
  isAdmin: boolean;
  unassignedLocationCount: number;
  unassignedLocations: IUnassignedLocationEntry[];
  settings: IRecruiterDashboardSettings | null;
  dateRangeLabel: string;
  dateRangeStart?: Date;
  dateRangeEnd?: Date;
  showLocationRecruitStats?: boolean;
  regionRecruitStats?: IRegionRecruitStats[];
  onUnassignedClick?: () => void;
  onDonorListClick?: (entry: IRecruiterStatsEntry) => void;
  onLocationListClick?: (entry: IRecruiterStatsEntry) => void;
  onRegionDonorListClick?: (region: IRegionRecruitStats) => void;
}

/** Small avatar circle matching leaderboard style */
const AvatarCircle: FC<{
  userId: string;
  firstName: string;
  lastName: string;
  size?: number;
  avatarUrl?: string;
}> = ({ userId, firstName, lastName, size = 36, avatarUrl }) => {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={`${firstName} ${lastName}`}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
        }}
      />
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: getUserColor(userId),
        fontSize: size * 0.38,
        fontWeight: 600,
        color: '#fff',
      }}
    >
      {getInitials(firstName, lastName)}
    </div>
  );
};

/** Pill-style action button matching TabLinkButton in the pipeline strip */
const RowActionButton: FC<{
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  iconColor?: string;
  bgColor?: string;
}> = ({ icon, label, onClick, iconColor = '#cedbef', bgColor = ROW_BG }) => {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setPressed(false);
      }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        cursor: 'pointer',
        fontSize: 10,
        background: hovered ? '#1a5a8a' : bgColor,
        padding: '4px 12px',
        borderRadius: 4,
        boxShadow: pressed
          ? '0 1px 2px rgba(0,0,0,0.2)'
          : '0 2px 4px rgba(0,0,0,0.25)',
        transform: pressed ? 'translateY(1px)' : 'translateY(0)',
        transition: 'all 0.15s ease',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontSize: 13, color: iconColor }}>{icon}</span>
      <span style={{ color: '#fff', fontSize: 10 }}>{label}</span>
    </div>
  );
};

/** Single row for a recruiter */
const RecruiterRow: FC<{
  entry: IRecruiterStatsEntry;
  index: number;
  settings: IRecruiterDashboardSettings | null;
  isMobile: boolean;
  recruitedTooltip?: React.ReactNode;
  onDonorListClick?: (entry: IRecruiterStatsEntry) => void;
  onLocationListClick?: (entry: IRecruiterStatsEntry) => void;
}> = ({
  entry,
  index,
  settings,
  isMobile,
  recruitedTooltip,
  onDonorListClick,
  onLocationListClick,
}) => {
  const avatarUrl = settings?.avatars?.[entry.userId];
  // Button bg is the opposite of the row bg (alternating)
  const btnBg = index % 2 === 0 ? ROW_BG_ALT : ROW_BG;
  const thresholdDays = settings?.activeLocationThresholdDays ?? 90;
  const activeLocTooltip = (
    <>
      Active locations / Total assigned locations <br />A location is considered
      &quot;active&quot; if it has had at least 1 donation within the last{' '}
      {thresholdDays} days.
    </>
  );

  return (
    <div
      style={{
        background: index % 2 === 0 ? ROW_BG : ROW_BG_ALT,
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        borderBottom: `1px solid ${DIVIDER_COLOR}`,
      }}
    >
      {/* Avatar */}
      <AvatarCircle
        userId={entry.userId}
        firstName={entry.firstName}
        lastName={entry.lastName}
        avatarUrl={avatarUrl}
      />

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Name + Region + Buttons — all in one flex row */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '4px 8px',
          }}
        >
          <Text
            style={{
              color: VALUE_COLOR,
              fontWeight: 600,
              fontSize: 13,
              whiteSpace: 'nowrap',
            }}
          >
            {entry.firstName} {entry.lastName}
          </Text>
          {entry.regionName && (
            <Text style={{ color: LABEL_COLOR, fontSize: 11 }}>
              {entry.regionName}
            </Text>
          )}
          <div
            style={{
              display: 'flex',
              gap: 4,
              marginLeft: 'auto',
            }}
          >
            <RowActionButton
              icon={<TableOutlined style={{ fontSize: 10 }} />}
              label="Donor List"
              onClick={() => onDonorListClick?.(entry)}
              bgColor={btnBg}
            />
            <RowActionButton
              icon={<EnvironmentOutlined style={{ fontSize: 10 }} />}
              label="Location List"
              onClick={() => onLocationListClick?.(entry)}
              bgColor={btnBg}
            />
          </div>
        </div>

        {/* Stats row */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '2px 20px',
            marginTop: 4,
          }}
        >
          {/* Recruited Donors */}
          {isMobile ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Text style={{ color: LABEL_COLOR, fontSize: 11 }}>
                Recruited Donors:
              </Text>
              <Text
                style={{ color: VALUE_COLOR, fontWeight: 700, fontSize: 13 }}
              >
                {entry.recruitedDonorCount}
              </Text>
              <DeltaIndicator
                current={entry.recruitedDonorCount}
                comparison={entry.prevRecruitedDonorCount}
                higherIsBetter
                size="small"
                darkBackground
              />
              <Popover
                content={
                  <span
                    style={{
                      maxWidth: 220,
                      display: 'block',
                      color: '#e8ecef',
                    }}
                  >
                    {recruitedTooltip}
                  </span>
                }
                trigger="click"
                placement="bottom"
                overlayStyle={{ maxWidth: 250 }}
                overlayInnerStyle={{
                  backgroundColor: '#0e4d8f',
                  color: '#e8ecef',
                }}
              >
                <InfoCircleOutlined
                  style={{
                    color: LABEL_COLOR,
                    fontSize: 12,
                    cursor: 'pointer',
                    marginLeft: 2,
                  }}
                />
              </Popover>
            </span>
          ) : (
            <Tooltip title={recruitedTooltip} placement="top">
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'help',
                }}
              >
                <Text style={{ color: LABEL_COLOR, fontSize: 11 }}>
                  Recruited Donors:
                </Text>
                <Text
                  style={{ color: VALUE_COLOR, fontWeight: 700, fontSize: 13 }}
                >
                  {entry.recruitedDonorCount}
                </Text>
                <DeltaIndicator
                  current={entry.recruitedDonorCount}
                  comparison={entry.prevRecruitedDonorCount}
                  higherIsBetter
                  size="small"
                  darkBackground
                />
              </span>
            </Tooltip>
          )}

          {/* Active Locations */}
          {isMobile ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Text style={{ color: LABEL_COLOR, fontSize: 11 }}>
                Active Locations:
              </Text>
              <Text
                style={{
                  color: VALUE_COLOR,
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {`${entry.activeLocationCount}/${entry.totalAssignedLocationCount}`}
              </Text>
              <Popover
                content={
                  <span
                    style={{
                      maxWidth: 220,
                      display: 'block',
                      color: '#e8ecef',
                    }}
                  >
                    {activeLocTooltip}
                  </span>
                }
                trigger="click"
                placement="bottom"
                overlayStyle={{ maxWidth: 250 }}
                overlayInnerStyle={{
                  backgroundColor: '#0e4d8f',
                  color: '#e8ecef',
                }}
              >
                <InfoCircleOutlined
                  style={{
                    color: LABEL_COLOR,
                    fontSize: 12,
                    cursor: 'pointer',
                    marginLeft: 2,
                  }}
                />
              </Popover>
            </span>
          ) : (
            <Tooltip title={activeLocTooltip} placement="top">
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'help',
                }}
              >
                <Text style={{ color: LABEL_COLOR, fontSize: 11 }}>
                  Active Locations:
                </Text>
                <Text
                  style={{
                    color: VALUE_COLOR,
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {`${entry.activeLocationCount}/${entry.totalAssignedLocationCount}`}
                </Text>
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

export const RecruiterStatsCard: FC<IRecruiterStatsCardProps> = ({
  entries,
  loading,
  isAdmin,
  unassignedLocationCount,
  settings,
  dateRangeLabel,
  dateRangeStart,
  dateRangeEnd,
  showLocationRecruitStats,
  regionRecruitStats,
  onUnassignedClick,
  onDonorListClick,
  onLocationListClick,
  onRegionDonorListClick,
}) => {
  const [btnHovered, setBtnHovered] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < MOBILE_BREAKPOINT;

  const dateRangeStr =
    dateRangeStart && dateRangeEnd
      ? `${dateRangeStart.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        })} – ${dateRangeEnd.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}`
      : '';

  const recruitedTooltip = (
    <>
      {dateRangeStr && (
        <>
          <strong>{dateRangeStr}</strong>
          <br />
        </>
      )}
      New donors registered during this period, attributed to the recruiter
      based on donor location match. The trend arrow compares to the previous
      equivalent period.
    </>
  );

  const thresholdDays = settings?.activeLocationThresholdDays ?? 90;
  const regionActiveLocTooltip = (
    <>
      Active locations / Total locations in this region
      <br />A location is considered &quot;active&quot; if it has had at least 1
      donation within the last {thresholdDays} days.
    </>
  );

  const regionRecruitedTooltip = (
    <>
      {dateRangeStr && (
        <>
          <strong>{dateRangeStr}</strong>
          <br />
        </>
      )}
      New donors registered during this period, attributed to the region based
      on the donor&apos;s assigned location. Independent of recruiter
      assignments. The trend arrow compares to the previous equivalent period.
    </>
  );

  return (
    <div
      style={{
        background: CARD_BG,
        borderRadius: 8,
        overflow: 'hidden',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div style={{ padding: '14px 16px 8px' }}>
        <Text
          style={{
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            display: 'block',
          }}
        >
          Recruiter Stats
        </Text>
        <Text style={{ color: LABEL_COLOR, fontSize: 11 }}>
          {dateRangeLabel}
        </Text>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: 32,
            }}
          >
            <Spin />
          </div>
        ) : entries.length === 0 ? (
          <div style={{ padding: '24px 16px', textAlign: 'center' }}>
            <Text style={{ color: LABEL_COLOR }}>
              No recruiter data available
            </Text>
          </div>
        ) : (
          entries.map((entry, i) => (
            <RecruiterRow
              key={entry.userId}
              entry={entry}
              index={i}
              settings={settings}
              isMobile={isMobile}
              recruitedTooltip={recruitedTooltip}
              onDonorListClick={onDonorListClick}
              onLocationListClick={onLocationListClick}
            />
          ))
        )}
      </div>

      {/* Location New Recruit Stats */}
      {showLocationRecruitStats &&
        regionRecruitStats &&
        regionRecruitStats.length > 0 && (
          <div>
            <div
              style={{
                padding: '10px 16px 6px',
                borderTop: `1px solid ${DIVIDER_COLOR}`,
              }}
            >
              <Text
                style={{
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 700,
                  display: 'block',
                }}
              >
                Location New Recruit Stats
              </Text>
              <Text style={{ color: LABEL_COLOR, fontSize: 11 }}>
                {dateRangeLabel} — by region
              </Text>
            </div>
            {regionRecruitStats.map((region, i) => (
              <div
                key={region.regionId}
                style={{
                  background: i % 2 === 0 ? ROW_BG : ROW_BG_ALT,
                  padding: '8px 14px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '4px 14px',
                  borderBottom: `1px solid ${DIVIDER_COLOR}`,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <Text
                    style={{
                      color: VALUE_COLOR,
                      fontWeight: 600,
                      fontSize: 13,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {region.regionName}
                  </Text>
                </div>
                {isMobile ? (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <Text style={{ color: LABEL_COLOR, fontSize: 11 }}>
                      Recruited Donors:
                    </Text>
                    <Text
                      style={{
                        color: VALUE_COLOR,
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      {region.recruitedDonorCount}
                    </Text>
                    <DeltaIndicator
                      current={region.recruitedDonorCount}
                      comparison={region.prevRecruitedDonorCount}
                      higherIsBetter
                      size="small"
                      darkBackground
                    />
                    <Popover
                      content={
                        <span
                          style={{
                            maxWidth: 220,
                            display: 'block',
                            color: '#e8ecef',
                          }}
                        >
                          {regionRecruitedTooltip}
                        </span>
                      }
                      trigger="click"
                      placement="bottom"
                      overlayStyle={{ maxWidth: 250 }}
                      overlayInnerStyle={{
                        backgroundColor: '#0e4d8f',
                        color: '#e8ecef',
                      }}
                    >
                      <InfoCircleOutlined
                        style={{
                          color: LABEL_COLOR,
                          fontSize: 12,
                          cursor: 'pointer',
                          marginLeft: 2,
                        }}
                      />
                    </Popover>
                  </div>
                ) : (
                  <Tooltip title={regionRecruitedTooltip} placement="top">
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        cursor: 'help',
                      }}
                    >
                      <Text style={{ color: LABEL_COLOR, fontSize: 11 }}>
                        Recruited Donors:
                      </Text>
                      <Text
                        style={{
                          color: VALUE_COLOR,
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {region.recruitedDonorCount}
                      </Text>
                      <DeltaIndicator
                        current={region.recruitedDonorCount}
                        comparison={region.prevRecruitedDonorCount}
                        higherIsBetter
                        size="small"
                        darkBackground
                      />
                    </div>
                  </Tooltip>
                )}
                {isMobile ? (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <Text style={{ color: LABEL_COLOR, fontSize: 11 }}>
                      Active Locations:
                    </Text>
                    <Text
                      style={{
                        color: VALUE_COLOR,
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      {region.activeLocationCount}/{region.totalLocationCount}
                    </Text>
                    <Popover
                      content={
                        <span
                          style={{
                            maxWidth: 220,
                            display: 'block',
                            color: '#e8ecef',
                          }}
                        >
                          {regionActiveLocTooltip}
                        </span>
                      }
                      trigger="click"
                      placement="bottom"
                      overlayStyle={{ maxWidth: 250 }}
                      overlayInnerStyle={{
                        backgroundColor: '#0e4d8f',
                        color: '#e8ecef',
                      }}
                    >
                      <InfoCircleOutlined
                        style={{
                          color: LABEL_COLOR,
                          fontSize: 12,
                          cursor: 'pointer',
                          marginLeft: 2,
                        }}
                      />
                    </Popover>
                  </div>
                ) : (
                  <Tooltip title={regionActiveLocTooltip} placement="top">
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        cursor: 'help',
                      }}
                    >
                      <Text style={{ color: LABEL_COLOR, fontSize: 11 }}>
                        Active Locations:
                      </Text>
                      <Text
                        style={{
                          color: VALUE_COLOR,
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {region.activeLocationCount}/{region.totalLocationCount}
                      </Text>
                    </div>
                  </Tooltip>
                )}
                <div style={{ marginLeft: 'auto' }}>
                  <RowActionButton
                    icon={<TableOutlined style={{ fontSize: 10 }} />}
                    label="Donor List"
                    onClick={() => onRegionDonorListClick?.(region)}
                    bgColor={i % 2 === 0 ? ROW_BG_ALT : ROW_BG}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Admin footer: Unassigned Locations button */}
      {isAdmin && unassignedLocationCount > 0 && (
        <div
          style={{
            padding: '8px 16px 10px',
            borderTop: `1px solid ${DIVIDER_COLOR}`,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              background: btnHovered ? '#1a5a8a' : ROW_BG,
              padding: '4px 12px',
              borderRadius: 4,
              boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
              transition: 'all 0.15s ease',
            }}
            onClick={onUnassignedClick}
            onMouseEnter={() => setBtnHovered(true)}
            onMouseLeave={() => setBtnHovered(false)}
          >
            <EnvironmentOutlined style={{ fontSize: 14, color: '#cedbef' }} />
            <span style={{ color: '#fff', fontSize: 11 }}>
              Unassigned Locations: {unassignedLocationCount}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecruiterStatsCard;
